import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import type { GetStartedContentInput } from '@bcc/shared';
import { apiUrl } from '../lib/apiUrl';
import { supabase } from '../lib/supabase';

/** Illustrative metrics for the Get Started spotlight card (not a filed offering). */
const SPOTLIGHT_DEAL = {
  slug: 'example-fl',
  title: 'Coastal Commerce Lofts',
  subtitle: 'Mixed-use podium · Tampa, FL',
  imageSrc: '/deal-spotlight-coastal-fl.jpg',
  /** Investor / tranche style classification (example only). */
  dealClass: 'Class B · value-add',
  /** Business plan summary. */
  strategy: 'Renovate amenity deck & unit interiors; mark-to-market rents across a 5-year hold',
  units: 312,
  locationRanking: 'Top Sun Belt MSA · 3-yr rent growth',
  populationGrowth5Yr: '+9.4%',
  underwriting: {
    projectSummary: [
      { label: 'Purchase Price', value: '$35.8M' },
      { label: 'Per Unit', value: '$110,494' },
      { label: 'Capex Amount', value: '$1.25M' },
      { label: 'Stabilization Time', value: '10 months' },
      { label: 'Hold Period', value: '3–5 years' },
      { label: 'Working Capital', value: '$330,000' },
      { label: 'Target Exit Price', value: '$50M' },
    ],
    keyAssumptions: [
      { label: 'Exit Cap Rate', value: '5.5%' },
      { label: 'Acquisition Debt', value: '$28.2M' },
      { label: 'Loan To Cost', value: '76%' },
      { label: 'Interest Rate', value: '4.65%' },
    ],
    footnote: 'Assumes a 3-year hold with a 5.5% exit cap rate.',
  },
} as const;

function resolveSpotlightImage(path: string | undefined, fallback: string): string {
  if (!path) return fallback;
  if (path.startsWith('http') || path.startsWith('/')) return path;
  return supabase.storage.from('deal-assets').getPublicUrl(path).data.publicUrl;
}

function buildSpotlight(cfg: Partial<GetStartedContentInput>) {
  const base = SPOTLIGHT_DEAL;
  const u = base.underwriting;
  return {
    slug: base.slug,
    title: cfg.spotlightTitle ?? base.title,
    subtitle: cfg.spotlightSubtitle ?? base.subtitle,
    imageSrc: resolveSpotlightImage(cfg.spotlightImagePath, base.imageSrc),
    dealClass: cfg.dealClass ?? base.dealClass,
    strategy: cfg.strategy ?? base.strategy,
    units: cfg.units ?? base.units,
    locationRanking: cfg.locationRanking ?? base.locationRanking,
    populationGrowth5Yr: cfg.populationGrowth5Yr ?? base.populationGrowth5Yr,
    underwriting: {
      projectSummary: cfg.underwritingProjectSummary?.length ? cfg.underwritingProjectSummary : [...u.projectSummary],
      keyAssumptions: cfg.underwritingKeyAssumptions?.length ? cfg.underwritingKeyAssumptions : [...u.keyAssumptions],
      footnote: cfg.underwritingFootnote ?? u.footnote,
    },
    linkedDealId: cfg.linkedDealId ?? null,
  };
}

function UnderwritingRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-baseline justify-between gap-3 border-b border-white/[0.1] py-2.5 last:border-b-0">
      <span className="font-body text-[0.78rem] font-medium text-[var(--home-muted-on-dark)]">{label}</span>
      <span className="shrink-0 text-right font-body text-[0.78rem] font-semibold tabular-nums text-[var(--home-text-on-dark)]">
        {value}
      </span>
    </li>
  );
}

const CLASS_A_GOLD = 'text-[#fde68a]';
const CLASS_B_BLUE = 'text-[var(--home-miami-blue)]';

const INVESTMENT_STRUCTURE_ROWS = [
  { id: 'a1', group: 'a' as const, cls: 'Class A-1', amount: '$1M+', ret: '10%', star: 'red' as const },
  { id: 'a2', group: 'a' as const, cls: 'Class A-2', amount: '$500K – $1M', ret: '9%', star: 'teal' as const },
  { id: 'a3', group: 'a' as const, cls: 'Class A-3', amount: '$250K – $499K', ret: '8%', star: null },
  { id: 'b1', group: 'b' as const, cls: 'Class B-1', amount: '$1M+', ret: '8%', star: null },
  { id: 'b2', group: 'b' as const, cls: 'Class B-2', amount: '$500K – $1M', ret: '7%', star: null },
  { id: 'b3', group: 'b' as const, cls: 'Class B-3', amount: '$50K – $499K', ret: '6%', star: 'orange' as const },
];

function TierStar({ tone }: { tone: 'red' | 'teal' | 'orange' }) {
  const cls =
    tone === 'red'
      ? 'text-red-400'
      : tone === 'teal'
        ? 'text-teal-400'
        : 'text-orange-400';
  return (
    <svg className={`h-3.5 w-3.5 ${cls}`} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2.2l2.7 6.9h7.2l-5.8 4.4 2.2 7.2-6.3-4.6-6.3 4.6 2.2-7.2-5.8-4.4h7.2L12 2.2z" />
    </svg>
  );
}

/** Debt slice = brand pink (`--header-shell-bg`); equity = Miami blue. Pure ring — no center fill. */
const DONUT_DEBT = '#f472b6';
const DONUT_EQUITY = '#54c3ea';

function CapitalStackDonut() {
  const r = 38;
  const stroke = 14;
  const c = 2 * Math.PI * r;
  const debtLen = c * 0.75;
  const eqLen = c * 0.25;

  return (
    <div className="flex flex-col items-center">
      <svg
        viewBox="0 0 100 100"
        className="h-44 w-44 shrink-0 drop-shadow-[0_6px_24px_rgb(0_0_0/0.35)]"
        role="img"
        aria-label="Capital stack: 75 percent debt, 25 percent equity"
      >
        <circle cx="50" cy="50" r={r + stroke / 2 - 0.5} fill="none" className="stroke-white/15" strokeWidth={0.75} />
        <g transform="rotate(-90 50 50)">
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={DONUT_DEBT}
            strokeWidth={stroke}
            strokeDasharray={`${debtLen} ${eqLen}`}
            strokeLinecap="butt"
          />
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={DONUT_EQUITY}
            strokeWidth={stroke}
            strokeDasharray={`${eqLen} ${debtLen}`}
            strokeDashoffset={-debtLen}
            strokeLinecap="butt"
          />
        </g>
      </svg>
      <ul className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 font-body text-[0.75rem] text-[var(--home-text-on-dark)]">
        <li className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-sm ring-1 ring-white/25"
            style={{ backgroundColor: DONUT_DEBT }}
            aria-hidden
          />
          Debt · 75%
        </li>
        <li className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-sm ring-1 ring-white/25"
            style={{ backgroundColor: DONUT_EQUITY }}
            aria-hidden
          />
          Equity · 25%
        </li>
      </ul>
    </div>
  );
}

/** Curved arrow from equity slice (left side of donut layout) toward the equity breakdown card. */
function EquityBridgeArrow({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width={108}
      height={56}
      viewBox="0 0 108 56"
      fill="none"
      aria-hidden
    >
      <defs>
        <marker id="equity-arrow-head" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto" markerUnits="strokeWidth">
          <path d="M0 0 L10 5 L0 10 Z" fill="#e8c468" />
        </marker>
      </defs>
      <path
        d="M 4 38 C 24 14, 52 6, 88 22 L 100 28"
        stroke="#e8c468"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        markerEnd="url(#equity-arrow-head)"
      />
    </svg>
  );
}

/** Marketing funnel — hero and deal summaries; registration lives at `/register`. */
export function GetStarted() {
  const [cfg, setCfg] = useState<Partial<GetStartedContentInput> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(apiUrl('/api/public/site-content/get_started'));
        const j = (await res.json()) as { value: Record<string, unknown> };
        if (!cancelled) setCfg((j.value as Partial<GetStartedContentInput>) ?? {});
      } catch {
        if (!cancelled) setCfg({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const spot = useMemo(() => buildSpotlight(cfg ?? {}), [cfg]);
  const profileHref = spot.linkedDealId ? `/deals/${spot.linkedDealId}` : `/deals/${spot.slug}`;

  return (
    <div className="mx-auto max-w-[90rem] px-4 py-14 md:px-8 lg:px-10 lg:py-20">
      <section className="relative overflow-hidden rounded-3xl border border-white/[0.1] shadow-[0_32px_90px_-40px_rgb(0_0_0/0.85)] ring-1 ring-black/35">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/get-started-hero.jpg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[var(--home-zone-void)]/66" aria-hidden />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.2]"
          aria-hidden
          style={{
            backgroundImage: `radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1.5px)`,
            backgroundSize: '20px 20px',
          }}
        />

        <div className="relative z-10 px-5 py-10 text-center sm:px-8 sm:py-12 md:px-10 md:py-14">
          <div
            className="mx-auto mb-6 h-px w-16 max-w-[85%] bg-gradient-to-r from-transparent via-[#e8c468] to-transparent opacity-95"
            aria-hidden
          />

          <p className="font-display text-[0.68rem] font-bold uppercase tracking-[0.24em] text-[#f0d78c]">
            {cfg?.heroLocationTag ?? 'Tampa, Florida'}
          </p>

          <h1 className="mt-4 font-display text-[clamp(1.75rem,5vw,3.25rem)] font-extrabold uppercase leading-[0.98] tracking-[0.02em] text-white">
            <span className="text-[var(--home-text-on-dark)]">DTR</span>{' '}
            <span className="text-[var(--header-shell-bg)]">×</span>{' '}
            <span className="text-[var(--home-text-on-dark)]">Programs</span>
            <span className="mx-2 inline-block align-middle sm:mx-3">
              <span className="inline-flex rounded-md border border-white/15 bg-black/75 px-2.5 py-1 font-display text-[0.5rem] font-bold uppercase tracking-[0.12em] text-white shadow-lg sm:text-[0.55rem]">
                + Private CRE
              </span>
            </span>
          </h1>

          <p className="font-[family-name:'Great_Vibes',cursive] text-[clamp(2rem,6vw,3.5rem)] leading-none text-[#fde68a]">
            Double The Return
          </p>

          {cfg?.heroSubtitle ? (
            <p className="mx-auto mt-4 max-w-xl font-body text-[0.95rem] leading-relaxed text-white/88 md:text-base">{cfg.heroSubtitle}</p>
          ) : (
            <>
              <p className="mx-auto mt-8 max-w-lg font-display text-lg font-bold leading-snug tracking-[0.02em] text-white md:text-xl">
                Commercial Real estate, rebuilt.
              </p>
              <p className="mx-auto mt-4 max-w-xl font-body text-[0.95rem] leading-relaxed text-white/88 md:text-base">
                We connect accredited investors to institutional multifamily and mixed-use deals—simple structure, clear reporting,
                real assets.
              </p>
              <p className="mx-auto mt-6 max-w-lg font-display text-base font-bold leading-snug tracking-[0.03em] text-white md:text-lg">
                No noise. Just ownership in quality markets.
              </p>
            </>
          )}

          <div className="mt-8 flex justify-center">
            <Link
              to="/register"
              className="inline-flex min-w-[14rem] items-center justify-center rounded-xl bg-[var(--home-miami-blue)] px-8 py-3.5 font-display text-[0.65rem] font-bold uppercase tracking-[0.16em] text-[var(--home-zone-void)] shadow-[0_12px_40px_-12px_rgb(84_195_234/0.65)] ring-1 ring-white/25 transition hover:brightness-105"
            >
              Get Access Now
            </Link>
          </div>

          <div className="mt-6 space-y-1 font-display text-[0.72rem] font-bold uppercase tracking-[0.1em] text-white/90">
            <p className="mb-1.5 text-[0.62rem] font-semibold tracking-[0.18em] text-[#f0d78c]">Minimum investment</p>
            <p>Class A = {cfg?.minClassA ?? '$250,000'}</p>
            <p>Class B = {cfg?.minClassB ?? '$50,000'}</p>
            <p className="pt-1 text-[var(--home-muted-on-dark)]">Allocations limited per close</p>
          </div>

          <div className="mx-auto mt-10 grid max-w-lg grid-cols-2 gap-3 sm:gap-4">
            <div className="rounded-xl border border-[var(--home-miami-blue)]/35 bg-[var(--home-zone-dark)]/70 p-4 text-left shadow-inner ring-1 ring-white/[0.04] backdrop-blur-sm">
              <p className="font-display text-sm font-bold uppercase tracking-[0.06em] text-[#fde68a]">Cash</p>
              <p className="mt-1 font-display text-[0.55rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
                Direct investment
              </p>
              <svg className="mt-3 h-8 w-8 text-[#e8c468]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.25} aria-hidden>
                <path d="M12 2v20M7 6h10M7 18h10" strokeLinecap="round" />
                <rect x="4" y="8" width="16" height="8" rx="1" />
              </svg>
            </div>
            <div className="rounded-xl border border-[var(--home-miami-blue)]/35 bg-[var(--home-zone-dark)]/70 p-4 text-left shadow-inner ring-1 ring-white/[0.04] backdrop-blur-sm">
              <p className="font-display text-sm font-bold uppercase tracking-[0.06em] text-[#fde68a]">401(k) / IRA</p>
              <p className="mt-1 font-display text-[0.55rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
                Self-directed
              </p>
              <svg className="mt-3 h-8 w-8 text-[#e8c468]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.25} aria-hidden>
                <path d="M4 20h16M6 20V10l6-4 6 4v10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 14h6M9 18h6" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10 md:mt-12" aria-labelledby="active-deal-heading">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2
              id="active-deal-heading"
              className="font-display text-xl font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] md:text-2xl"
            >
              Active deal
            </h2>
            <p className="mt-1 max-w-2xl font-body text-[0.9rem] text-[var(--home-muted-on-dark)]">
              Featured example program — diligence against the private placement memorandum for any live series.
            </p>
          </div>
          <span className="inline-flex w-fit items-center rounded-full border border-[var(--home-miami-blue)]/45 bg-[var(--home-miami-blue)]/[0.12] px-3 py-1.5 font-display text-[0.58rem] font-bold uppercase tracking-[0.14em] text-[var(--home-miami-blue)]">
            Now allocating
          </span>
        </div>

        <article className="overflow-hidden rounded-3xl border border-white/[0.1] bg-[var(--home-zone-dark)]/55 shadow-[0_28px_70px_-40px_rgb(0_0_0/0.65)] ring-1 ring-white/[0.05] backdrop-blur-md">
          <div className="grid gap-0 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,1fr)]">
            <div className="relative aspect-[4/3] min-h-[14rem] sm:aspect-[16/10] sm:min-h-[16rem] lg:aspect-auto lg:min-h-[22rem]">
              <img
                src={spot.imageSrc}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--home-zone-void)]/90 via-[var(--home-zone-void)]/15 to-[var(--home-zone-void)]/25 lg:bg-gradient-to-r lg:from-[var(--home-zone-void)]/20 lg:via-transparent lg:to-[var(--home-zone-void)]/5"
                aria-hidden
              />

              <div className="absolute left-3 top-3 z-10 max-w-[min(75%,220px)] sm:max-w-[min(65%,200px)]">
                <span className="inline-block rounded-md bg-neutral-950/75 px-2.5 py-1 font-display text-[0.56rem] font-semibold uppercase leading-tight tracking-[0.05em] text-white ring-1 ring-white/10 backdrop-blur-sm">
                  {spot.dealClass}
                </span>
              </div>
              <div className="absolute right-3 top-3 z-10 flex max-w-[min(88%,300px)] flex-col items-end gap-1.5 text-right">
                <span className="rounded-md bg-[var(--header-shell-bg)]/92 px-2.5 py-1.5 text-left font-body text-[0.72rem] font-medium leading-snug text-white shadow-md shadow-black/20 ring-1 ring-white/25 backdrop-blur-sm">
                  {spot.strategy}
                </span>
              </div>
            </div>

            <div className="flex flex-col p-6 md:p-8">
              <header className="border-b border-white/[0.08] pb-5">
                <h3 className="font-display text-lg font-bold uppercase tracking-[0.06em] text-white md:text-xl">
                  {spot.title}
                </h3>
                <p className="mt-1.5 font-body text-[0.95rem] text-[var(--home-muted-on-dark)]">{spot.subtitle}</p>
              </header>

              <dl className="mt-6 grid flex-1 grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-5">
                <div>
                  <dt className="font-display text-[0.6rem] font-bold uppercase tracking-[0.18em] text-[#f0d78c]">Units</dt>
                  <dd className="mt-2 font-display text-2xl font-bold tabular-nums text-[var(--home-text-on-dark)]">
                    {spot.units.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="font-display text-[0.6rem] font-bold uppercase tracking-[0.18em] text-[#f0d78c]">Location ranking</dt>
                  <dd className="mt-2 font-body text-[0.9rem] leading-snug text-[var(--home-text-on-dark)]">
                    {spot.locationRanking}
                  </dd>
                </div>
                <div>
                  <dt className="font-display text-[0.6rem] font-bold uppercase tracking-[0.18em] text-[#f0d78c]">
                    Population growth · next 5 years
                  </dt>
                  <dd className="mt-2 font-display text-xl font-bold tabular-nums text-[var(--home-miami-blue)] md:text-2xl">
                    {spot.populationGrowth5Yr}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 border-t border-white/[0.06] pt-6">
                <h4 className="font-body text-[1.1rem] font-semibold leading-snug tracking-tight text-[var(--home-text-on-dark)] md:text-[1.2rem]">
                  Underwriting assumptions
                </h4>

                <div className="mt-5 grid gap-8 md:grid-cols-2 md:gap-8 lg:gap-10">
                  <div>
                    <p className="mb-1 border-b border-white/15 pb-2 font-display text-[0.62rem] font-bold uppercase tracking-[0.14em] text-[#f0d78c]">
                      Project summary
                    </p>
                    <ul className="list-none">
                      {spot.underwriting.projectSummary.map((row) => (
                        <UnderwritingRow key={row.label} label={row.label} value={row.value} />
                      ))}
                    </ul>
                  </div>

                  <div>
                    <p className="mb-1 border-b border-white/15 pb-2 font-display text-[0.62rem] font-bold uppercase tracking-[0.14em] text-[#f0d78c]">
                      Key assumptions
                    </p>
                    <ul className="list-none">
                      {spot.underwriting.keyAssumptions.map((row) => (
                        <UnderwritingRow key={row.label} label={row.label} value={row.value} />
                      ))}
                    </ul>
                  </div>
                </div>

                <p className="mt-5 font-body text-[0.68rem] leading-snug italic text-[var(--home-muted-on-dark)]">
                  {spot.underwriting.footnote}
                </p>
              </div>

              <div className="mt-8 flex flex-col gap-3 border-t border-white/[0.08] pt-6 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-body text-[0.72rem] leading-relaxed text-[var(--home-muted-on-dark)]">
                  Sample metrics for layout only — confirm targets, fees, and risk factors in the formal offering documents.
                </p>
                <Link
                  to={profileHref}
                  className="inline-flex shrink-0 items-center justify-center rounded-xl bg-[var(--header-shell-bg)] px-5 py-2.5 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-white shadow-md shadow-black/25 ring-1 ring-white/20 transition hover:brightness-110"
                >
                  View deal profile
                </Link>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="mt-10 md:mt-12" aria-labelledby="investment-structure-heading">
        <article className="rounded-3xl border border-white/[0.1] bg-[var(--home-zone-dark)]/55 p-6 shadow-[0_28px_70px_-40px_rgb(0_0_0/0.65)] ring-1 ring-white/[0.05] backdrop-blur-md md:p-8">
          <header className="border-b border-white/[0.08] pb-6">
            <h2
              id="investment-structure-heading"
              className="font-display text-xl font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] md:text-2xl"
            >
              Investment structure &amp; returns
            </h2>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-8 gap-y-2">
              <p className="font-display text-[0.72rem] font-bold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Minimum investment{' '}
                <span className={`text-[1rem] tracking-normal ${CLASS_A_GOLD}`}>Class A · $250,000</span>
              </p>
              <p className="font-display text-[0.72rem] font-bold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Minimum investment{' '}
                <span className={`text-[1rem] tracking-normal ${CLASS_B_BLUE}`}>Class B · $50,000</span>
              </p>
            </div>
          </header>

          <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:gap-12">
            <div className="min-w-0">
              <div className="overflow-x-auto rounded-xl border border-white/10 ring-1 ring-white/[0.04]">
                <table className="w-full min-w-[320px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-white/15 bg-white/[0.04]">
                      <th className="px-3 py-3 font-display text-[0.58rem] font-bold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)] md:px-4">
                        Class type
                      </th>
                      <th className="px-3 py-3 font-display text-[0.58rem] font-bold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)] md:px-4">
                        Investment amount
                      </th>
                      <th className="px-3 py-3 font-display text-[0.58rem] font-bold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)] md:px-4">
                        Avg annualized return %
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {INVESTMENT_STRUCTURE_ROWS.map((row) => (
                      <tr
                        key={row.id}
                        className={`border-b border-white/[0.08] last:border-b-0 ${row.group === 'a' ? 'bg-[#fde68a]/[0.04]' : 'bg-[var(--home-miami-blue)]/[0.06]'}`}
                      >
                        <td
                          className={`px-3 py-2.5 font-display text-[0.72rem] font-bold uppercase tracking-[0.06em] md:px-4 ${
                            row.group === 'a' ? CLASS_A_GOLD : CLASS_B_BLUE
                          }`}
                        >
                          {row.cls}
                        </td>
                        <td className="px-3 py-2.5 font-body text-[0.82rem] text-[var(--home-text-on-dark)] md:px-4 md:text-[0.85rem]">
                          {row.amount}
                        </td>
                        <td className="px-3 md:px-4">
                          <span className="inline-flex items-center gap-1.5 font-display text-[0.85rem] font-bold tabular-nums text-white">
                            {row.ret}
                            {row.star ? <TierStar tone={row.star} /> : null}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 font-body text-[0.7rem] leading-relaxed text-[var(--home-muted-on-dark)]">
                Illustrative tiers — refer to the offering memorandum for definitive economics, waterfalls, and risk factors.
              </p>
            </div>

            <div className="space-y-10">
              <div>
                <h3
                  className={`border-b border-[#e8c468]/55 pb-2 font-display text-[0.72rem] font-bold uppercase tracking-[0.14em] ${CLASS_A_GOLD}`}
                >
                  Preferred equity (Class A)
                </h3>
                <ul className="mt-4 space-y-2.5 font-body text-[0.88rem] leading-relaxed text-[var(--home-text-on-dark)]">
                  <li>
                    <span className="text-[var(--home-muted-on-dark)]">Returns: </span>
                    8% – 10% avg annualized preferred return (priority).
                  </li>
                  <li>
                    <span className="text-[var(--home-muted-on-dark)]">Distributions: </span>
                    Quarterly distributions.
                  </li>
                  <li>
                    <span className="text-[var(--home-muted-on-dark)]">Objective: </span>
                    Focus on risk mitigation.
                  </li>
                  <li>
                    <span className="text-[var(--home-muted-on-dark)]">Upside: </span>
                    No participation in economic upside.
                  </li>
                </ul>
                <p className="mt-3 flex flex-wrap items-center gap-2 font-body text-[0.7rem] italic text-[var(--home-muted-on-dark)]">
                  <span className="inline-flex items-center gap-1" aria-hidden>
                    <TierStar tone="red" />
                    <TierStar tone="teal" />
                  </span>
                  See Example Slides
                </p>
              </div>

              <div>
                <h3
                  className={`border-b border-[var(--home-miami-blue)]/45 pb-2 font-display text-[0.72rem] font-bold uppercase tracking-[0.14em] ${CLASS_B_BLUE}`}
                >
                  Common equity (Class B)
                </h3>
                <ul className="mt-4 space-y-2.5 font-body text-[0.88rem] leading-relaxed text-[var(--home-text-on-dark)]">
                  <li>
                    <span className="text-[var(--home-muted-on-dark)]">Returns: </span>
                    6% – 8% avg annualized preferred return (subordinate).
                  </li>
                  <li>
                    <span className="block text-[var(--home-muted-on-dark)]">Investor / sponsor split schedule</span>
                    <span className="mt-1 block pl-0 sm:pl-1">
                      <strong className={CLASS_B_BLUE}>B-1:</strong> 85% investor / 15% sponsor
                    </span>
                    <span className="mt-1 block sm:pl-1">
                      <strong className={CLASS_B_BLUE}>B-2:</strong> 80% investor / 20% sponsor
                    </span>
                    <span className="mt-1 block sm:pl-1">
                      <strong className={CLASS_B_BLUE}>B-3:</strong> 75% investor / 25% sponsor
                    </span>
                  </li>
                  <li>
                    <span className="text-[var(--home-muted-on-dark)]">Upside: </span>
                    Participation in economic upside.
                  </li>
                </ul>
                <p className="mt-3 flex flex-wrap items-center gap-2 font-body text-[0.7rem] italic text-[var(--home-muted-on-dark)]">
                  <TierStar tone="orange" />
                  See Example Slides
                </p>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="mt-10 md:mt-12" aria-labelledby="financial-highlights-heading">
        <article className="rounded-3xl border border-white/[0.1] bg-[var(--home-zone-dark)]/55 p-6 shadow-[0_28px_70px_-40px_rgb(0_0_0/0.65)] ring-1 ring-white/[0.05] backdrop-blur-md md:p-8">
          <header className="border-b border-white/[0.08] pb-6">
            <h2
              id="financial-highlights-heading"
              className="font-body text-2xl font-semibold uppercase leading-tight tracking-[0.04em] text-[var(--home-text-on-dark)] md:text-[1.65rem]"
            >
              Investment financial highlights
            </h2>
          </header>

          <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-12">
            <div className="divide-y divide-white/[0.1] rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-2 ring-1 ring-white/[0.04]">
              <div className="py-4 first:pt-3">
                <p className="font-body text-[0.9rem] text-[var(--home-muted-on-dark)]">Target IRR</p>
                <p className="mt-1 font-display text-3xl font-bold tabular-nums tracking-tight text-[var(--home-miami-blue)] md:text-4xl">
                  17%+
                </p>
              </div>
              <div className="py-4">
                <p className="font-body text-[0.9rem] text-[var(--home-muted-on-dark)]">Equity multiplier</p>
                <p className="mt-1 font-display text-2xl font-bold tabular-nums text-[var(--home-miami-blue)] md:text-3xl">
                  1.7x – 2.10x+
                </p>
                <p className="mt-2 font-body text-[0.68rem] leading-snug italic text-[var(--home-muted-on-dark)]">
                  *Exact EM will be dependent on exit year and cap rate.
                </p>
              </div>
              <div className="py-4 last:pb-3">
                <p className="font-body text-[0.9rem] text-[var(--home-muted-on-dark)]">Average cash flow</p>
                <p className="mt-1 font-display text-2xl font-bold tabular-nums text-[var(--home-miami-blue)] md:text-3xl">
                  6% – 8%
                </p>
                <p className="mt-2 font-body text-[0.68rem] leading-snug italic text-[var(--home-muted-on-dark)]">
                  *Cash flow increases as renovations are completed and operations stabilize.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-center font-display text-base font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">
                Capital stack
              </h3>
              <div className="mt-8 flex flex-col items-center gap-10 lg:flex-row lg:items-start lg:justify-center lg:gap-6">
                <CapitalStackDonut />

                <div className="flex w-full max-w-md flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-center lg:w-auto lg:max-w-none">
                  <EquityBridgeArrow className="shrink-0 -rotate-90 text-[#e8c468] sm:rotate-0" />
                  <div className="w-full min-w-[12rem] rounded-2xl border border-white/10 bg-white/[0.03] p-5 ring-1 ring-white/[0.04]">
                    <h4 className="border-b border-[var(--home-miami-blue)]/35 pb-2 font-display text-[0.72rem] font-bold uppercase tracking-[0.12em] text-[var(--home-miami-blue)]">
                      Equity breakdown
                    </h4>
                    <ul className="mt-3 space-y-2 font-body text-[0.9rem] text-[var(--home-text-on-dark)]">
                      <li className={`font-semibold ${CLASS_A_GOLD}`}>30% Class A</li>
                      <li className={`font-semibold ${CLASS_B_BLUE}`}>70% Class B</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-8 font-body text-[0.7rem] leading-relaxed italic text-[var(--home-muted-on-dark)]">
            Figures shown are for discussion only and are not a guarantee of performance — review the private placement memorandum and suitability with your advisors.
          </p>
        </article>
      </section>
    </div>
  );
}
